import type { ItemProps } from '@tryhackme/thm-ui-components/dist/components/atoms/desktop-nav/desktop-nav.types';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { UserRoles } from 'src/common/enums';
import { useFeatureIsOn } from 'src/common/growthbook';
import { useHasRole } from 'src/common/hooks/use-user-has-role';
export const UseIsNavItemActive = (item: ItemProps) => {
  const location = useLocation();
  const isRoomMaker = useHasRole(UserRoles.ROOM_MAKER);
  const isNewNavEnabled = useFeatureIsOn('new-navigation');
  const activeSubItem = useMemo(() => {
    if (!item.subItems) return undefined;
    return item.subItems.find(subItem => {
      if (subItem.href === location.pathname) return true;
      if (subItem.subMenuItems) {
        return subItem.subMenuItems.some(nestedItem => {
          if (!nestedItem.href) return false;

          // Exact match first
          if (nestedItem.href === location.pathname) return true;

          // Special handling for certification pages
          // Only match if we're actually on a certification page
          if (location.pathname.startsWith('/certification/') && nestedItem.href.startsWith('/certification/')) {
            // Extract base certification path (remove /details or other suffixes)
            const certBasePath = nestedItem.href.replace(/\/(details|overview|exam)$/, '');
            const currentCertBasePath = location.pathname.replace(/\/(details|overview|exam)$/, '');
            return certBasePath === currentCertBasePath;
          }
          return false;
        });
      }
      return false;
    });
  }, [item.subItems, location.pathname]);
  const isNavItemActive = useMemo(() => {
    const isUserInRoom = location.pathname.startsWith('/room/');
    const isLearnSection = item.name === 'Learn';
    const isManageRooms = location.pathname === '/rooms' && location.search.includes('tab=manage-rooms');
    const isSimPath = location.pathname.startsWith('/soc-sim') || location.pathname.startsWith('/threat-hunting-sim');
    const isCertificationPath = location.pathname.startsWith('/certification/');
    const isManagementDashboardPath = location.pathname.startsWith('/client/');
    const shouldHighlightSim = isSimPath && ((isNewNavEnabled ? item.name === 'Practice' : item.name === 'Learn') || Boolean(activeSubItem));
    const shouldHighlightCert = isCertificationPath && (isNewNavEnabled ? item.name === 'Practice' : item.name === 'Learn');
    if (isUserInRoom && !isLearnSection) return Boolean(activeSubItem);
    if (isUserInRoom) return isLearnSection;
    if (location.pathname === '/paths' && item.name === 'Learn' || location.pathname === '/your-material/custom-development' && item.name === 'Develop' || location.pathname === '/your-material/content-engineers' && item.name === 'Develop' || location.pathname === '/develop-rooms' && item.name === 'Develop' || isManageRooms && item.name === 'Develop' || shouldHighlightSim || shouldHighlightCert) {
      return true;
    }
    if (item.name === 'Other' && isRoomMaker) return false;
    if (item.name === 'Business' && isManagementDashboardPath) return true;
    return Boolean(activeSubItem);
  }, [activeSubItem, item.name, location.pathname, location.search, isRoomMaker, isNewNavEnabled]);
  return isNavItemActive;
};